import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Box,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import SecurityIcon from '@mui/icons-material/Security';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DevicesIcon from '@mui/icons-material/Devices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WindowsIcon from '@mui/icons-material/Window';
import PageHeader from '../../components/common/PageHeader';
import api from '../../api/axiosInstance';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';

const DEVICE_COLORS = ['#0f766e', '#2563eb', '#f59e0b', '#7c3aed', '#ef4444', '#0891b2', '#84cc16', '#ec4899'];

const normalizeSerial = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLevelSerial = (device = {}) => device.serial_number || device.serialNumber || device.serial || device.hardware?.serialNumber || '';
const getEsetSerial = (device = {}) => device.hardware?.[0]?.serialNumber || device.serialNumber || device.serial_number || '';

const getLevelName = (device = {}) => device.name || device.hostname || device.computer_name || device.device_name || '';
const getEsetName = (device = {}) => device.name || device.hostname || '';
const getAgentOrganizationName = (agent = {}) => agent.Organization?.name || agent.organization?.name || agent.organizationName || '';
const getAgentInventory = (agent = {}) => agent.lastInventory || {};
const getAgentSerial = (agent = {}) => {
  const inventory = getAgentInventory(agent);
  const hardware = inventory.hardware || {};
  return hardware.serialNumber || hardware.serial || hardware.bios?.serialNumber || inventory.raw?.hardware?.serialNumber || '';
};
const getAgentInstallDate = (agent = {}) => {
  const inventory = getAgentInventory(agent);
  return inventory.os?.installDate || inventory.os?.installedAt || inventory.operatingSystem?.installDate
    || inventory.system?.installDate || inventory.raw?.os?.installDate || inventory.raw?.operatingSystem?.installDate || '';
};
const getAgentLoggedUser = (agent = {}) => {
  const users = getAgentInventory(agent).loggedUsers;
  if (!Array.isArray(users) || !users.length) return '';
  return users.map((user) => typeof user === 'string' ? user : (user.name || user.username || user.userName || user.user || user.displayName || '')).filter(Boolean).join(', ');
};
const getAgentSite = (agent = {}) => {
  const inventory = getAgentInventory(agent);
  const siteValue = agent.customSite || agent.siteName || agent.site?.name || agent.site || agent.siteRef?.name
    || inventory.siteName || inventory.site?.name || inventory.site || '';
  return typeof siteValue === 'string' ? siteValue : (siteValue?.name || '');
};
const formatDeviceAge = (value) => {
  if (!value) return '';
  const installed = new Date(value);
  if (Number.isNaN(installed.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - installed.getFullYear();
  let months = now.getMonth() - installed.getMonth();
  if (now.getDate() < installed.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  return `${years} an${years > 1 ? 's' : ''}${months ? ` ${months} mois` : ''}`;
};

const getLevelOrganizationName = (device = {}) => device.matchedOrganizationName || device.matchedOrganization?.name || device.organizationName || '';
const getEsetOrganizationName = (device = {}) => device.organizationDetails?.name || device.organizationName || '';

const getLevelOs = (device = {}) => device.operating_system?.name || device.os?.name || device.operatingSystem || '';
const getLevelOsVersion = (device = {}) => device.operating_system?.version || device.os?.version || '';
const getLevelGroup = (device = {}) => device.matchedLevelGroupPath || device.levelGroupName || device.group?.name || '';

const componentNames = (device = {}) => Array.isArray(device.components)
  ? device.components.map((component) => component.name).filter(Boolean).join(', ')
  : '';

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

export default function ClientDevices() {
  const organizationNames = useClientOrganizationNames();
  const { data: esetDevices = [], loading: esetLoading, error: esetError } = useSelector((state) => state.eset.devices);
  const [levelDevices, setLevelDevices] = useState([]);
  const [levelLoading, setLevelLoading] = useState(true);
  const [levelError, setLevelError] = useState('');
  const [agentDevices, setAgentDevices] = useState([]);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentError, setAgentError] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);

  useEffect(() => {
    let mounted = true;
    setLevelLoading(true);
    setLevelError('');
    api.get('/api/level/devices?includeDeleted=false')
      .then(({ data }) => {
        if (mounted) setLevelDevices(data.devices || []);
      })
      .catch((error) => {
        if (mounted) setLevelError(error.response?.data?.message || error.response?.data?.error || 'Impossible de charger les appareils Level.io.');
      })
      .finally(() => {
        if (mounted) setLevelLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setAgentLoading(true);
    setAgentError('');
    api.get('/api/agents')
      .then(({ data }) => {
        if (mounted) setAgentDevices(data.data || data.agents || []);
      })
      .catch((error) => {
        if (mounted) setAgentError(error.response?.data?.message || error.response?.data?.error || 'Impossible de charger les appareils AgentBOX.');
      })
      .finally(() => mounted && setAgentLoading(false));
    return () => { mounted = false; };
  }, []);

  const clientName = organizationNames[0] || 'Client';

  const filteredLevelDevices = useMemo(() => {
    const hasOrganizationName = levelDevices.some((device) => getLevelOrganizationName(device));
    if (!organizationNames.length || !hasOrganizationName) return levelDevices;
    return levelDevices.filter((device) => organizationNames.includes(getLevelOrganizationName(device)));
  }, [levelDevices, organizationNames]);

  const filteredEsetDevices = useMemo(() => {
    const activeDevices = esetDevices.filter((device) => !device.deleted);
    const hasOrganizationName = activeDevices.some((device) => getEsetOrganizationName(device));
    if (!organizationNames.length || !hasOrganizationName) return activeDevices;
    return activeDevices.filter((device) => organizationNames.includes(getEsetOrganizationName(device)));
  }, [esetDevices, organizationNames]);

  const filteredAgentDevices = useMemo(() => {
    const activeAgents = agentDevices.filter((agent) => !agent.deleted);
    const hasOrganizationName = activeAgents.some((agent) => getAgentOrganizationName(agent));
    if (!organizationNames.length || !hasOrganizationName) return activeAgents;
    return activeAgents.filter((agent) => organizationNames.includes(getAgentOrganizationName(agent)));
  }, [agentDevices, organizationNames]);

  const rows = useMemo(() => {
    const bySerial = new Map();
    const addRow = (key, payload) => {
      if (!bySerial.has(key)) bySerial.set(key, { serialKey: key, level: null, eset: null, agent: null });
      Object.assign(bySerial.get(key), payload);
    };

    filteredLevelDevices.forEach((device, index) => {
      const serial = normalizeSerial(getLevelSerial(device));
      addRow(serial || `level-${device.id || device.device_id || index}`, { level: device });
    });

    filteredEsetDevices.forEach((device, index) => {
      const serial = normalizeSerial(getEsetSerial(device));
      addRow(serial || `eset-${device._id || device.id || index}`, { eset: device });
    });

    filteredAgentDevices.forEach((agent, index) => {
      const serial = normalizeSerial(getAgentSerial(agent));
      addRow(serial || `agent-${agent.agentId || agent._id || index}`, { agent });
    });

    return Array.from(bySerial.values()).map((row, index) => {
      const level = row.level || {};
      const eset = row.eset || {};
      const agent = row.agent || {};
      const inventory = getAgentInventory(agent);
      const hardware = inventory.hardware || {};
      const resolvedOs = [getLevelOs(level), getLevelOsVersion(level), inventory.os?.name, inventory.os?.version, inventory.operatingSystem?.name].filter(Boolean).join(' ');
      const isServerOs = /windows\s*server|debian|ubuntu|centos|rhel|red\s*hat|rocky|alma.?linux|suse|proxmox|esxi|server/i.test(resolvedOs);
      const levelSerial = getLevelSerial(level);
      const esetSerial = getEsetSerial(eset);
      return {
        id: row.serialKey || index,
        serialNumber: levelSerial || esetSerial || getAgentSerial(agent) || '',
        name: getLevelName(level) || getEsetName(eset) || agent.hostname || agent.agentId || '',
        hostname: level.hostname || eset.hostname || agent.hostname || getLevelName(level) || getEsetName(eset) || '',
        agentUser: getAgentLoggedUser(agent),
        site: getAgentSite(agent),
        manufacturer: level.manufacturer || eset.hardware?.[0]?.manufacturer || hardware.vendor || hardware.manufacturer || '',
        model: level.model || eset.hardware?.[0]?.model || hardware.model || '',
        osInstallDate: getAgentInstallDate(agent),
        ageGroup: getAgentInstallDate(agent) && new Date(getAgentInstallDate(agent)) < new Date(new Date().setFullYear(new Date().getFullYear() - 4)) ? 'Plus de 4 ans' : 'Récent',
        deviceAge: formatDeviceAge(getAgentInstallDate(agent)),
        deviceType: resolvedOs ? (isServerOs ? 'Serveur' : 'Poste') : 'Inconnu',
        client: getLevelOrganizationName(level) || getEsetOrganizationName(eset) || getAgentOrganizationName(agent),
        levelGroup: getLevelGroup(level),
        levelStatus: level.status || '',
        levelLastSeen: formatDate(level.last_seen_at || level.last_check_in || level.updated_at),
        levelOs: resolvedOs,
        esetTenant: eset.tenantName || '',
        esetLastSync: formatDate(eset.lastSync),
        ipLan: eset.ipLAN || level.ip_address || (Array.isArray(inventory.network?.interfaces) ? inventory.network.interfaces.flatMap((item) => item.addresses || item.ips || []).join(', ') : ''),
        ipWan: eset.ipWAN || level.public_ip_address || inventory.network?.publicIp || '',
        esetComponents: componentNames(eset),
        inLevel: !!row.level,
        inEset: !!row.eset,
        inAgent: !!row.agent,
      };
    }).sort((a, b) => String(a.name || a.serialNumber).localeCompare(String(b.name || b.serialNumber)));
  }, [filteredLevelDevices, filteredEsetDevices, filteredAgentDevices]);

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Nom',
      size: 220,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorKey: 'serialNumber',
      header: 'N° série',
      size: 180,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => renderedCellValue || <Typography variant="body2" color="text.secondary">Non renseigné</Typography>,
    },
    {
      accessorFn: (row) => [row.inLevel ? 'Level.io' : '', row.inEset ? 'ESET' : '', row.inAgent ? 'AgentBOX' : ''].filter(Boolean).join(' '),
      id: 'sources',
      header: 'Produit',
      size: 180,
      filterVariant: 'multi-select',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.75}>
          {row.original.inLevel ? <Chip size="small" icon={<ComputerIcon />} label="Level.io" color="primary" variant="outlined" /> : null}
          {row.original.inEset ? <Chip size="small" icon={<SecurityIcon />} label="ESET" color="success" variant="outlined" /> : null}
          {row.original.inAgent ? <Chip size="small" icon={<MonitorHeartIcon />} label="AgentBOX" color="secondary" variant="outlined" /> : null}
        </Stack>
      ),
    },
    { accessorKey: 'hostname', header: 'Hostname', size: 210, filterVariant: 'autocomplete' },
    { accessorKey: 'agentUser', header: 'Utilisateur', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'site', header: 'Site', size: 160, filterVariant: 'autocomplete' },
    { accessorKey: 'manufacturer', header: 'Fabricant', size: 160, filterVariant: 'autocomplete' },
    { accessorKey: 'model', header: 'Modèle', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'levelGroup', header: 'Dossier Level.io', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'levelStatus', header: 'Statut Level', size: 140, filterVariant: 'autocomplete' },
    { accessorKey: 'levelLastSeen', header: 'Vu par Level', size: 170 },
    { accessorKey: 'levelOs', header: 'OS', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'ageGroup', header: 'Âge', size: 120, enableHiding: true },
    { accessorKey: 'deviceType', header: 'Type', size: 120, enableHiding: true },
    { accessorKey: 'esetTenant', header: 'Tenant ESET', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'esetLastSync', header: 'Synchro ESET', size: 170 },
    { accessorKey: 'ipLan', header: 'IP LAN', size: 140 },
    { accessorKey: 'ipWan', header: 'IP WAN', size: 140 },
    { accessorKey: 'esetComponents', header: 'Composants ESET', size: 260 },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_FR,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableFacetedValues: true,
    enableGlobalFilter: true,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 25 },
      columnPinning: { left: ['hostname'] },
      columnOrder: ['hostname', 'manufacturer', 'model', 'levelOs', 'agentUser', 'site', 'sources', 'name', 'serialNumber', 'levelGroup', 'levelStatus', 'levelLastSeen', 'ageGroup', 'deviceType', 'esetTenant', 'esetLastSync', 'ipLan', 'ipWan', 'esetComponents'],
      columnVisibility: {
        name: false,
        serialNumber: false,
        levelGroup: false,
        levelStatus: false,
        levelLastSeen: false,
        esetTenant: false,
        esetLastSync: false,
        ipLan: false,
        ipWan: false,
        esetComponents: false,
        ageGroup: false,
        deviceType: false,
      },
    },
    enableExpandAll: false,
    renderDetailPanel: ({ row }) => {
      const device = row.original;
      return (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} divider={<Box sx={{ borderLeft: { md: '1px solid' }, borderColor: 'divider' }} />}>
            <Stack spacing={0.5} sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Appareil</Typography>
              <Typography variant="body2">OS : {device.levelOs || '-'}</Typography>
              <Typography variant="body2">Fabricant : {device.manufacturer || '-'}</Typography>
              <Typography variant="body2">Modèle : {device.model || '-'}</Typography>
              <Typography variant="body2">Hostname : {device.hostname || '-'}</Typography>
              <Typography variant="body2">Age : {device.deviceAge || '-'}</Typography>
              <Typography variant="body2">Nom : {device.name || '-'}</Typography>
              <Typography variant="body2">N° série : {device.serialNumber || '-'}</Typography>
            </Stack>
            <Stack spacing={0.5} sx={{ minWidth: 260 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Supervision</Typography>
              <Typography variant="body2">Level.io : {device.levelGroup || (device.inLevel ? 'Rattaché' : 'Non présent')}</Typography>
              <Typography variant="body2">Tenant ESET : {device.esetTenant || (device.inEset ? 'Rattaché' : 'Non présent')}</Typography>
              <Typography variant="body2">Dernière synchro ESET : {device.esetLastSync || '-'}</Typography>
              <Typography variant="body2">Dernière remontée Level : {device.levelLastSeen || '-'}</Typography>
            </Stack>
            <Stack spacing={0.5} sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Réseau et composants</Typography>
              <Typography variant="body2">IP LAN : {device.ipLan || '-'}</Typography>
              <Typography variant="body2">IP WAN : {device.ipWan || '-'}</Typography>
              <Typography variant="body2">Composants ESET : {device.esetComponents || '-'}</Typography>
            </Stack>
          </Stack>
        </Box>
      );
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Rechercher',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    positionGlobalFilter: 'right',
    state: { isLoading: levelLoading || esetLoading || agentLoading, columnFilters },
  });

  const loading = levelLoading || esetLoading || agentLoading;

  const manufacturerData = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const manufacturer = String(row.manufacturer || 'Inconnu').trim() || 'Inconnu';
      counts.set(manufacturer, (counts.get(manufacturer) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [rows]);

  const oldDevices = useMemo(() => {
    const limit = new Date();
    limit.setFullYear(limit.getFullYear() - 4);
    return rows.filter((row) => row.osInstallDate && new Date(row.osInstallDate) < limit).length;
  }, [rows]);

  const windows10Devices = useMemo(() => rows.filter((row) => /windows\s*10/i.test(row.levelOs || '')).length, [rows]);
  const deviceTypeData = useMemo(() => ['Serveur', 'Poste', 'Inconnu'].map((name) => ({ name, value: rows.filter((row) => row.deviceType === name).length })).filter((item) => item.value > 0), [rows]);
  const applyFilter = (id, value) => setColumnFilters(value ? [{ id, value }] : []);
  const glassCardSx = {
    border: '1px solid', borderColor: 'rgba(52,211,153,.24)', borderRadius: 3,
    background: 'linear-gradient(135deg, rgba(15,118,110,.16), rgba(255,255,255,.04) 55%, rgba(132,204,22,.12))',
    backdropFilter: 'blur(14px)', boxShadow: '0 12px 28px rgba(15,118,110,.12)', cursor: 'pointer',
  };

  return (
    <Box>
      <PageHeader section={clientName} title="Vos appareils" sx={{ my: 1, mb: 3 }} />

      

      <Grid container spacing={1.5} sx={{mb:2}}>
        <Grid size={{xs:12, md:4}} onClick={() => applyFilter('name', '')} sx={{ ...glassCardSx, flex: 1, minHeight: 130, p: 1.75, gridColumn: { lg: 'span 2' }, gridRow: { lg: 1 } }}>
          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <DevicesIcon color="primary" />
            <Box><Typography variant="body2" color="text.secondary">Nombre d'appareils</Typography><Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{rows.length}</Typography></Box>
          </Stack>
        </Grid>
       
        <Grid size={{xs:12, md:4}} onClick={() => applyFilter('ageGroup', 'Plus de 4 ans')} sx={{ ...glassCardSx, flex: 1, minHeight: 130, p: 1.75, borderColor: oldDevices ? 'warning.main' : 'rgba(52,211,153,.24)', background: oldDevices ? 'linear-gradient(135deg, rgba(245,158,11,.18), rgba(255,255,255,.04))' : glassCardSx.background }}>
          <Stack direction="row" spacing={1.25} alignItems="center"><WarningAmberIcon fontSize="large" color={oldDevices ? 'warning' : 'disabled'} /><Box><Typography variant="body2" color="text.secondary">PC âgés de plus de 4 ans</Typography><Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{oldDevices}</Typography></Box></Stack>
        </Grid>
        <Grid size={{xs:12, md:4}} onClick={() => applyFilter('levelOs', 'Windows 10')} sx={{ ...glassCardSx, flex: 1, minHeight: 130, p: 1.75, borderColor: windows10Devices ? 'error.main' : 'rgba(52,211,153,.24)', background: windows10Devices ? 'linear-gradient(135deg, rgba(239,68,68,.18), rgba(255,255,255,.04))' : glassCardSx.background }}>
          <Stack direction="row" spacing={1.25} alignItems="center"><WindowsIcon fontSize="large" color={windows10Devices ? 'error' : 'disabled'} /><Box><Typography variant="body2" color="text.secondary">Windows 10</Typography><Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{windows10Devices}</Typography></Box></Stack>
        </Grid>
      </Grid>

      {levelError ? <Alert severity="error" sx={{ mb: 2 }}>{levelError}</Alert> : null}
      {esetError ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des appareils ESET.</Alert> : null}
      {agentError ? <Alert severity="error" sx={{ mb: 2 }}>{agentError}</Alert> : null}

      {loading && !rows.length ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucun appareil trouvé</Typography>
        </Box>
      ) : (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, lg: 9 }}><MaterialReactTable table={table} /></Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ ...glassCardSx, p: 1.5, minHeight: 220, overflow: 'visible' }}><Typography variant="body2" color="text.secondary">Répartition des fabricants</Typography><MuiPieChart width={360} height={190} tooltip={{ trigger: 'item', anchor: 'pointer', popperOptions: { strategy: 'fixed' } }} series={[{ data: manufacturerData.map((item, index) => ({ id: item.name, label: item.name, value: item.value, color: DEVICE_COLORS[index % DEVICE_COLORS.length] })), innerRadius: 38, outerRadius: 72, valueFormatter: (value) => `${value} (${((Number(value) / Math.max(1, rows.length)) * 100).toFixed(1)}%)` }]} slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' } } }} /></Box>
              <Box sx={{ ...glassCardSx, p: 1.5, minHeight: 220, overflow: 'visible' }}><Typography variant="body2" color="text.secondary">Répartition serveurs / postes</Typography><MuiPieChart width={360} height={190} tooltip={{ trigger: 'item', anchor: 'pointer', popperOptions: { strategy: 'fixed' } }} series={[{ data: deviceTypeData.map((item, index) => ({ id: item.name, label: item.name, value: item.value, color: DEVICE_COLORS[index % DEVICE_COLORS.length] })), innerRadius: 38, outerRadius: 72, valueFormatter: (value) => `${value} (${((Number(value) / Math.max(1, rows.length)) * 100).toFixed(1)}%)` }]} slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' } } }} /></Box>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
